import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const API = (process.env.REACT_APP_API_URL || '') + '/admin';
const PWD_KEY = 'admin_pwd';
const WINDOWS = ['today', 'yesterday', 'last_7_days', 'last_30_days', 'lifetime'] as const;

type WindowKey = typeof WINDOWS[number];

type UsageWindow = {
  label: WindowKey;
  start_at?: string | null;
  end_at?: string | null;
  total_requests: number;
  total_tokens: number;
  total_microreports: number;
};

type UsageFeatureRow = {
  feature_key: string;
  total_requests: number;
  total_tokens: number;
  total_microreports: number;
};

type UserAnalyticsWindow = {
  total_requests: number;
  total_tokens: number;
  total_microreports: number;
};

type UserAnalytics = {
  user_id: string;
  email: string | null;
  windows: Record<WindowKey, UserAnalyticsWindow>;
};

type ContentLogSession = {
  id: string;
  created_at: string;
  surface: string;
  agent_id: string | null;
  model: string | null;
  message_count: number;
  retention_expires_at: string;
};

type ContentLogMessage = {
  id: string;
  created_at: string;
  role: string;
  message_text: string;
  message_kind: string;
  excluded_context_summary?: Record<string, unknown> | null;
};

type CreditTxn = {
  id: string;
  amount: number;
  type: string;
  description: string | null;
  reference_id: string | null;
  created_at: string;
};

type CreditHistory = {
  user_id: string;
  email: string | null;
  balance: number;
  transactions: CreditTxn[];
};

type MonthlyCreditRow = {
  month: string;
  issued: number;
  used: number;
  net: number;
  transaction_count: number;
};

type MonthlyCreditSummary = {
  user_id: string;
  email: string | null;
  months: MonthlyCreditRow[];
};

export default function AdminPanel() {
  const nav = useNavigate();
  const [pwd, setPwd] = useState('');
  const [loggedIn, setLoggedIn] = useState(() => !!sessionStorage.getItem(PWD_KEY));
  const [users, setUsers] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [sel, setSel] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const [amt, setAmt] = useState('');
  const [perms, setPerms] = useState({ competitive: true, market_research: true, customer_research: true });
  const [usageWindows, setUsageWindows] = useState<UsageWindow[]>([]);
  const [usageFeatures, setUsageFeatures] = useState<Record<string, UsageFeatureRow[]>>({});
  const [userAnalytics, setUserAnalytics] = useState<UserAnalytics | null>(null);
  const [userAnalyticsLoading, setUserAnalyticsLoading] = useState(false);
  const userAnalyticsAbortRef = React.useRef<AbortController | null>(null);
  const [contentLogs, setContentLogs] = useState<ContentLogSession[]>([]);
  const [contentLogDetail, setContentLogDetail] = useState<{ session: ContentLogSession; messages: ContentLogMessage[] } | null>(null);
  const [activeTab, setActiveTab] = useState<'users' | 'user_details' | 'analytics' | 'content'>('users');
  const [contentSurface, setContentSurface] = useState('');

  const [detailSearch, setDetailSearch] = useState('');
  const [detailDropdownOpen, setDetailDropdownOpen] = useState(false);
  const [detailUser, setDetailUser] = useState<any>(null);
  const [detailCreditHistory, setDetailCreditHistory] = useState<CreditHistory | null>(null);
  const [detailMonthlySummary, setDetailMonthlySummary] = useState<MonthlyCreditSummary | null>(null);
  const [detailUserAnalytics, setDetailUserAnalytics] = useState<UserAnalytics | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const detailDropdownRef = React.useRef<HTMLDivElement>(null);

  const loadAnalytics = useCallback(async (password: string) => {
    if (!password) return;
    const [summaryRes, featuresRes] = await Promise.all([
      fetch(`${API}/usage/summary?password=${encodeURIComponent(password)}`),
      fetch(`${API}/usage/features?password=${encodeURIComponent(password)}`),
    ]);
    if ([summaryRes, featuresRes].some(r => r.status === 401)) {
      logout();
      return;
    }
    if (!summaryRes.ok || !featuresRes.ok) {
      throw new Error('Failed to load analytics');
    }
    const [summaryData, featuresData] = await Promise.all([summaryRes.json(), featuresRes.json()]);
    setUsageWindows(summaryData.windows || []);
    setUsageFeatures(featuresData.windows || {});
  }, []);

  const loadContentLogs = useCallback(async (password: string) => {
    if (!password) return;
    const qs = new URLSearchParams({ password, ...(contentSurface ? { surface: contentSurface } : {}) });
    const res = await fetch(`${API}/content-logs?${qs.toString()}`);
    if (res.status === 401) {
      logout();
      return;
    }
    if (!res.ok) {
      throw new Error('Failed to load content logs');
    }
    const data = await res.json();
    setContentLogs(data.sessions || []);
  }, [contentSurface]);

  const loadContentLogDetail = useCallback(async (id: string) => {
    const password = sessionStorage.getItem(PWD_KEY) || '';
    if (!password) return;
    const res = await fetch(`${API}/content-logs/${id}?password=${encodeURIComponent(password)}`);
    if (res.status === 401) {
      logout();
      return;
    }
    if (!res.ok) {
      throw new Error('Failed to load content log detail');
    }
    const data = await res.json();
    setContentLogDetail(data);
  }, []);

  const loadDetailUser = useCallback(async (user: any) => {
    setDetailUser(user);
    setDetailCreditHistory(null);
    setDetailMonthlySummary(null);
    setDetailUserAnalytics(null);
    if (!user) return;
    const password = sessionStorage.getItem(PWD_KEY) || '';
    if (!password) return;
    setDetailLoading(true);
    try {
      const [creditRes, monthlyRes, analyticsRes] = await Promise.all([
        fetch(`${API}/users/${user.id}/credit-history?password=${encodeURIComponent(password)}`),
        fetch(`${API}/users/${user.id}/credit-summary-monthly?password=${encodeURIComponent(password)}`),
        fetch(`${API}/usage/users/${user.id}?password=${encodeURIComponent(password)}`),
      ]);
      if ([creditRes, monthlyRes, analyticsRes].some(r => r.status === 401)) { logout(); return; }
      if (creditRes.ok) setDetailCreditHistory(await creditRes.json());
      if (monthlyRes.ok) setDetailMonthlySummary(await monthlyRes.json());
      if (analyticsRes.ok) setDetailUserAnalytics(await analyticsRes.json());
    } catch { /* ignore */ }
    setDetailLoading(false);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (detailDropdownRef.current && !detailDropdownRef.current.contains(e.target as Node)) {
        setDetailDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const load = useCallback(async (password: string) => {
    if (!password) return;
    setLoading(true);
    setErr('');
    try {
      const r = await fetch(`${API}/users?password=${encodeURIComponent(password)}&email=${encodeURIComponent(search)}`);
      if (r.status === 401) { sessionStorage.removeItem(PWD_KEY); setLoggedIn(false); setUsers([]); return; }
      if (!r.ok) { setErr('Failed to load users'); setLoading(false); return; }
      const d = await r.json();
      setUsers(d.users || []);
      await loadAnalytics(password);
      await loadContentLogs(password);
    } catch (e) { setErr('Network error'); }
    setLoading(false);
  }, [search, loadAnalytics, loadContentLogs]);

  useEffect(() => { if (loggedIn) { const p = sessionStorage.getItem(PWD_KEY) || ''; load(p); } }, [loggedIn, load]);

  const login = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pwd.trim()) { setErr('Enter password'); return; }
    setLoading(true);
    setErr('');
    try {
      const r = await fetch(`${API}/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password: pwd }) });
      if (!r.ok) { setErr('Wrong password'); setLoading(false); return; }
      sessionStorage.setItem(PWD_KEY, pwd);
      setLoggedIn(true);
      setPwd('');
    } catch { setErr('Login failed'); }
    setLoading(false);
  };

  const logout = () => { sessionStorage.removeItem(PWD_KEY); setLoggedIn(false); setUsers([]); setSel(null); };

  const addCredits = async () => {
    if (!sel || !amt) return;
    const n = parseFloat(amt);
    if (isNaN(n)) { setErr('Invalid number'); return; }
    const password = sessionStorage.getItem(PWD_KEY) || '';
    setLoading(true); setErr(''); setOk('');
    try {
      const r = await fetch(`${API}/users/${sel.id}/credits`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password, amount: n }) });
      if (r.status === 401) { logout(); return; }
      const d = await r.json();
      setOk(`Done. Balance: ${d.balance}`);
      setSel({ ...sel, credits: d.balance });
      setAmt('');
      load(password);
    } catch { setErr('Failed'); }
    setLoading(false);
  };

  const savePerms = async () => {
    if (!sel) return;
    const password = sessionStorage.getItem(PWD_KEY) || '';
    setLoading(true); setErr(''); setOk('');
    try {
      const r = await fetch(`${API}/users/${sel.id}/perms`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password, ...perms }) });
      if (r.status === 401) { logout(); return; }
      setOk('Permissions saved');
      load(password);
    } catch { setErr('Failed'); }
    setLoading(false);
  };

  const deleteUser = async () => {
    if (!sel) return;
    const confirmed = window.confirm(`Delete user ${sel.email || sel.id}? This cannot be undone.`);
    if (!confirmed) return;
    const password = sessionStorage.getItem(PWD_KEY) || '';
    setLoading(true); setErr(''); setOk('');
    try {
      const r = await fetch(`${API}/users/${sel.id}`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password }) });
      if (r.status === 401) { logout(); return; }
      if (!r.ok) { setErr('Failed to delete user'); setLoading(false); return; }
      setUsers(prev => prev.filter(u => u.id !== sel.id));
      setSel(null);
      setOk('User deleted');
    } catch {
      setErr('Failed to delete user');
    }
    setLoading(false);
  };

  const loadUserAnalytics = useCallback(async (userId: string, password: string) => {
    if (userAnalyticsAbortRef.current) {
      userAnalyticsAbortRef.current.abort();
    }
    const abortController = new AbortController();
    userAnalyticsAbortRef.current = abortController;

    setUserAnalyticsLoading(true);
    try {
      const res = await fetch(
        `${API}/usage/users/${userId}?password=${encodeURIComponent(password)}`,
        { signal: abortController.signal }
      );
      if (res.status === 401) {
        logout();
        return;
      }
      if (!res.ok) {
        throw new Error('Failed to load analytics');
      }
      const data = await res.json();
      setUserAnalytics(data);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      setUserAnalytics(null);
    } finally {
      setUserAnalyticsLoading(false);
    }
  }, []);

  const selectUser = (u: any) => {
    setSel(u);
    setPerms({ competitive: u.competitive, market_research: u.market_research, customer_research: u.customer_research });
    const password = sessionStorage.getItem(PWD_KEY) || '';
    loadUserAnalytics(u.id, password);
    setAmt(''); setErr(''); setOk('');
  };

  if (!loggedIn) return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0f172a, #1e1b4b)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <form onSubmit={login} style={{ background: '#1e293b', padding: 32, borderRadius: 12, width: 320 }}>
        <h2 style={{ margin: '0 0 24px', textAlign: 'center' }}>Admin</h2>
        {err && <p style={{ color: '#fca5a5', marginBottom: 16 }}>{err}</p>}
        <input type="password" placeholder="Password" value={pwd} onChange={e => setPwd(e.target.value)}
          style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: '#fff', marginBottom: 16 }} />
        <button disabled={loading} style={{ width: '100%', padding: 12, borderRadius: 8, border: 'none', background: loading ? '#334155' : '#6366f1', color: '#fff', cursor: loading ? 'wait' : 'pointer' }}>
          {loading ? 'Checking...' : 'Login'}
        </button>
        <button type="button" onClick={() => nav('/')} style={{ width: '100%', marginTop: 12, padding: 8, background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>Back</button>
      </form>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0f172a, #1e1b4b)', color: '#fff', padding: 24, position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflowY: 'scroll' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', paddingBottom: 100 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
          <h1>Admin</h1>
          <div>
            <button onClick={logout} style={{ marginRight: 8, padding: '8px 16px', background: '#334155', border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer' }}>Logout</button>
            <button onClick={() => nav('/')} style={{ padding: '8px 16px', background: '#334155', border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer' }}>Back</button>
          </div>
        </div>
        {err && <p style={{ background: '#7f1d1d', padding: 12, borderRadius: 6, marginBottom: 16 }}>{err}</p>}
        {ok && <p style={{ background: '#14532d', padding: 12, borderRadius: 6, marginBottom: 16 }}>{ok}</p>}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {[
            { key: 'users', label: 'Users' },
            { key: 'user_details', label: 'User Details' },
            { key: 'analytics', label: 'Analytics' },
            { key: 'content', label: 'Anonymous Content' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as typeof activeTab)}
              style={{ padding: '10px 14px', borderRadius: 8, border: activeTab === tab.key ? '1px solid #818cf8' : '1px solid #334155', background: activeTab === tab.key ? '#312e81' : '#111827', color: '#fff', cursor: 'pointer' }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {activeTab === 'user_details' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* User selector */}
            <div ref={detailDropdownRef} style={{ position: 'relative', maxWidth: 480 }}>
              <input
                value={detailSearch}
                onChange={e => { setDetailSearch(e.target.value); setDetailDropdownOpen(true); }}
                onFocus={() => setDetailDropdownOpen(true)}
                placeholder="Search by email and select a user..."
                style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: '#fff', fontSize: 14 }}
              />
              {detailDropdownOpen && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: '#1e293b', border: '1px solid #334155', borderRadius: 8, maxHeight: 280, overflowY: 'auto', zIndex: 50 }}>
                  {users
                    .filter(u => !detailSearch || (u.email || '').toLowerCase().includes(detailSearch.toLowerCase()))
                    .slice(0, 20)
                    .map(u => (
                      <button
                        key={u.id}
                        onClick={() => { loadDetailUser(u); setDetailSearch(u.email || u.id); setDetailDropdownOpen(false); }}
                        style={{ width: '100%', textAlign: 'left', padding: '10px 14px', border: 'none', borderBottom: '1px solid #334155', background: detailUser?.id === u.id ? '#312e81' : 'transparent', color: '#fff', cursor: 'pointer', fontSize: 14 }}
                      >
                        <div>{u.email || 'No email'}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>{u.credits} credits · {u.id.slice(0, 8)}…</div>
                      </button>
                    ))}
                  {users.filter(u => !detailSearch || (u.email || '').toLowerCase().includes(detailSearch.toLowerCase())).length === 0 && (
                    <div style={{ padding: 14, color: '#64748b', textAlign: 'center' }}>No matching users</div>
                  )}
                </div>
              )}
            </div>

            {!detailUser && (
              <div style={{ background: '#1e293b', borderRadius: 12, padding: 48, textAlign: 'center', color: '#64748b' }}>
                Select a user above to view their details
              </div>
            )}

            {detailUser && detailLoading && (
              <div style={{ background: '#1e293b', borderRadius: 12, padding: 48, textAlign: 'center', color: '#94a3b8' }}>
                Loading user data...
              </div>
            )}

            {detailUser && !detailLoading && (
              <>
                {/* User header card */}
                <div style={{ background: '#1e293b', borderRadius: 12, padding: 20, display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
                  <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 700, flexShrink: 0 }}>
                    {(detailUser.email || '?')[0].toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontSize: 18, fontWeight: 600 }}>{detailUser.email || 'No email'}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>ID: {detailUser.id}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    <div style={{ background: '#0f172a', borderRadius: 8, padding: '12px 20px', textAlign: 'center', border: '1px solid #334155' }}>
                      <div style={{ fontSize: 24, fontWeight: 700, color: '#a5b4fc' }}>{detailCreditHistory?.balance ?? detailUser.credits}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', marginTop: 2 }}>Balance</div>
                    </div>
                    {(() => {
                      const lastGrant = detailCreditHistory?.transactions.find(t => t.amount > 0);
                      return (
                        <div style={{ background: '#0f172a', borderRadius: 8, padding: '12px 20px', textAlign: 'center', border: '1px solid #334155' }}>
                          <div style={{ fontSize: 24, fontWeight: 700, color: '#f59e0b' }}>{lastGrant ? `+${lastGrant.amount.toFixed(1)}` : '—'}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', marginTop: 2 }}>Last Issued</div>
                          {lastGrant && <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>{new Date(lastGrant.created_at).toLocaleDateString()}</div>}
                        </div>
                      );
                    })()}
                    <div style={{ background: '#0f172a', borderRadius: 8, padding: '12px 20px', textAlign: 'center', border: '1px solid #334155' }}>
                      <div style={{ fontSize: 24, fontWeight: 700, color: '#34d399' }}>{detailCreditHistory?.transactions.filter(t => t.amount > 0).reduce((s, t) => s + t.amount, 0).toFixed(1) ?? '0'}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', marginTop: 2 }}>Total Issued</div>
                    </div>
                    <div style={{ background: '#0f172a', borderRadius: 8, padding: '12px 20px', textAlign: 'center', border: '1px solid #334155' }}>
                      <div style={{ fontSize: 24, fontWeight: 700, color: '#cbd5e1' }}>{detailCreditHistory?.transactions.length ?? 0}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', marginTop: 2 }}>Transactions</div>
                    </div>
                  </div>
                </div>

                {/* Monthly credit summary */}
                {detailMonthlySummary && detailMonthlySummary.months.length > 0 && (() => {
                  const maxVal = Math.max(...detailMonthlySummary.months.map(m => Math.max(m.issued, m.used)), 1);
                  return (
                    <div style={{ background: '#1e293b', borderRadius: 12, padding: 20 }}>
                      <h4 style={{ margin: '0 0 16px', fontSize: 14, textTransform: 'uppercase', color: '#94a3b8', letterSpacing: 1 }}>Monthly Credit Summary</h4>
                      <div style={{ display: 'grid', gap: 8 }}>
                        {detailMonthlySummary.months.map(m => {
                          const monthLabel = (() => { const [y, mo] = m.month.split('-'); return new Date(+y, +mo - 1).toLocaleString('default', { month: 'short', year: 'numeric' }); })();
                          return (
                            <div key={m.month} style={{ display: 'grid', gridTemplateColumns: '90px 1fr 1fr 80px 50px', gap: 12, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #1f2937' }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>{monthLabel}</div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ height: 18, borderRadius: 4, background: '#059669', width: `${Math.max((m.issued / maxVal) * 100, 2)}%`, minWidth: 4 }} />
                                <span style={{ fontSize: 12, color: '#34d399', whiteSpace: 'nowrap' }}>+{m.issued.toFixed(1)}</span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ height: 18, borderRadius: 4, background: '#dc2626', width: `${Math.max((m.used / maxVal) * 100, 2)}%`, minWidth: 4 }} />
                                <span style={{ fontSize: 12, color: '#f87171', whiteSpace: 'nowrap' }}>-{m.used.toFixed(1)}</span>
                              </div>
                              <div style={{ fontSize: 13, fontWeight: 600, color: m.net >= 0 ? '#34d399' : '#f87171', textAlign: 'right' }}>
                                {m.net >= 0 ? '+' : ''}{m.net.toFixed(1)}
                              </div>
                              <div style={{ fontSize: 11, color: '#64748b', textAlign: 'right' }}>{m.transaction_count} txn</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* Usage analytics row */}
                {detailUserAnalytics && (
                  <div style={{ background: '#1e293b', borderRadius: 12, padding: 20 }}>
                    <h4 style={{ margin: '0 0 12px', fontSize: 14, textTransform: 'uppercase', color: '#94a3b8', letterSpacing: 1 }}>Usage by Period</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
                      {WINDOWS.map(w => {
                        const d = detailUserAnalytics.windows[w];
                        return (
                          <div key={w} style={{ background: '#0f172a', borderRadius: 8, padding: 14, border: '1px solid #334155' }}>
                            <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#64748b', marginBottom: 8 }}>{w.replace(/_/g, ' ')}</div>
                            <div style={{ fontSize: 20, fontWeight: 700, color: '#e2e8f0' }}>{d?.total_microreports ?? 0}</div>
                            <div style={{ fontSize: 12, color: '#94a3b8' }}>microreports</div>
                            <div style={{ marginTop: 6, fontSize: 12, color: '#64748b' }}>{d?.total_requests ?? 0} requests · {d?.total_tokens ?? 0} tokens</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Credit transaction history */}
                <div style={{ background: '#1e293b', borderRadius: 12, padding: 20 }}>
                  <h4 style={{ margin: '0 0 16px', fontSize: 14, textTransform: 'uppercase', color: '#94a3b8', letterSpacing: 1 }}>Credit History</h4>
                  {(!detailCreditHistory || detailCreditHistory.transactions.length === 0) ? (
                    <div style={{ padding: 24, textAlign: 'center', color: '#64748b' }}>No credit transactions</div>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #334155' }}>
                            <th style={{ textAlign: 'left', padding: '8px 12px', color: '#64748b', fontWeight: 600 }}>Date</th>
                            <th style={{ textAlign: 'right', padding: '8px 12px', color: '#64748b', fontWeight: 600 }}>Amount</th>
                            <th style={{ textAlign: 'left', padding: '8px 12px', color: '#64748b', fontWeight: 600 }}>Type</th>
                            <th style={{ textAlign: 'left', padding: '8px 12px', color: '#64748b', fontWeight: 600 }}>Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailCreditHistory.transactions.map(t => (
                            <tr key={t.id} style={{ borderBottom: '1px solid #1f2937' }}>
                              <td style={{ padding: '10px 12px', color: '#94a3b8', whiteSpace: 'nowrap' }}>{new Date(t.created_at).toLocaleString()}</td>
                              <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: t.amount > 0 ? '#34d399' : t.amount < 0 ? '#f87171' : '#cbd5e1' }}>
                                {t.amount > 0 ? '+' : ''}{t.amount.toFixed(1)}
                              </td>
                              <td style={{ padding: '10px 12px' }}>
                                <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: t.type === 'welcome_bonus' ? '#064e3b' : t.type === 'admin_adjustment' ? '#312e81' : t.type === 'consumption' ? '#7f1d1d' : '#1f2937', color: t.type === 'welcome_bonus' ? '#6ee7b7' : t.type === 'admin_adjustment' ? '#a5b4fc' : t.type === 'consumption' ? '#fca5a5' : '#94a3b8' }}>
                                  {t.type.replace(/_/g, ' ')}
                                </span>
                              </td>
                              <td style={{ padding: '10px 12px', color: '#94a3b8' }}>{t.description || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
        {activeTab === 'analytics' && (
          <div style={{ display: 'grid', gap: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
              {WINDOWS.map(windowKey => {
                const item = usageWindows.find(entry => entry.label === windowKey);
                return (
                  <div key={windowKey} style={{ background: '#1e293b', borderRadius: 12, padding: 16, border: '1px solid #334155' }}>
                    <div style={{ fontSize: 12, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 8 }}>{windowKey.replaceAll('_', ' ')}</div>
                    <div style={{ fontSize: 14, color: '#cbd5e1' }}>Requests: {item?.total_requests ?? 0}</div>
                    <div style={{ fontSize: 14, color: '#cbd5e1' }}>Tokens: {item?.total_tokens ?? 0}</div>
                    <div style={{ fontSize: 14, color: '#cbd5e1' }}>Microanalyses: {item?.total_microreports ?? 0}</div>
                  </div>
                );
              })}
            </div>
            <div style={{ background: '#1e293b', borderRadius: 12, padding: 16 }}>
              <h3 style={{ marginTop: 0 }}>Feature Breakdown</h3>
              {WINDOWS.map(windowKey => (
                <div key={windowKey} style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 700, marginBottom: 8 }}>{windowKey.replaceAll('_', ' ')}</div>
                  {(usageFeatures[windowKey] || []).slice(0, 8).map(row => (
                    <div key={`${windowKey}-${row.feature_key}`} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: 8, padding: '6px 0', borderBottom: '1px solid #334155' }}>
                      <span>{row.feature_key}</span>
                      <span>{row.total_requests} req</span>
                      <span>{row.total_tokens} tok</span>
                      <span>{row.total_microreports} mr</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
        {activeTab === 'content' && (
          <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: 20 }}>
            <div style={{ background: '#1e293b', borderRadius: 12, padding: 16 }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <input value={contentSurface} onChange={e => setContentSurface(e.target.value)} placeholder="Filter surface" style={{ flex: 1, padding: 8, borderRadius: 6, border: '1px solid #334155', background: '#0f172a', color: '#fff' }} />
                <button onClick={() => loadContentLogs(sessionStorage.getItem(PWD_KEY) || '')} style={{ padding: '8px 12px', borderRadius: 6, border: 'none', background: '#6366f1', color: '#fff', cursor: 'pointer' }}>Load</button>
              </div>
              <div style={{ maxHeight: 600, overflow: 'auto' }}>
                {contentLogs.map(item => (
                  <button key={item.id} onClick={() => void loadContentLogDetail(item.id)} style={{ width: '100%', textAlign: 'left', marginBottom: 8, padding: 12, borderRadius: 8, border: '1px solid #334155', background: contentLogDetail?.session.id === item.id ? '#312e81' : '#0f172a', color: '#fff', cursor: 'pointer' }}>
                    <div>{item.surface}</div>
                    <div style={{ fontSize: 12, color: '#94a3b8' }}>{new Date(item.created_at).toLocaleString()} · {item.message_count} messages</div>
                  </button>
                ))}
              </div>
            </div>
            <div style={{ background: '#1e293b', borderRadius: 12, padding: 16 }}>
              {contentLogDetail ? (
                <>
                  <h3 style={{ marginTop: 0 }}>{contentLogDetail.session.surface}</h3>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 16 }}>Anonymous session {contentLogDetail.session.id}</div>
                  <div style={{ display: 'grid', gap: 12 }}>
                    {contentLogDetail.messages.map(message => (
                      <div key={message.id} style={{ padding: 12, borderRadius: 8, background: '#0f172a', border: '1px solid #334155' }}>
                        <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>{message.role} · {message.message_kind} · {new Date(message.created_at).toLocaleString()}</div>
                        <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{message.message_text}</div>
                      </div>
                    ))}
                  </div>
                </>
              ) : <p style={{ textAlign: 'center', color: '#64748b' }}>Select an anonymous content log session</p>}
            </div>
          </div>
        )}
        {activeTab === 'users' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          <div style={{ background: '#1e293b', borderRadius: 12, padding: 16 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <input placeholder="Search email" value={search} onChange={e => setSearch(e.target.value)}
                style={{ flex: 1, padding: 8, borderRadius: 6, border: '1px solid #334155', background: '#0f172a', color: '#fff' }} />
              <button onClick={() => load(sessionStorage.getItem(PWD_KEY) || '')} disabled={loading} style={{ padding: '8px 16px', background: '#6366f1', border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer' }}>Search</button>
            </div>
            <div style={{ maxHeight: 500, overflow: 'auto' }}>
              {loading && users.length === 0 && <p style={{ textAlign: 'center', color: '#64748b' }}>Loading...</p>}
              {users.map(u => (
                <div key={u.id} onClick={() => selectUser(u)}
                  style={{ padding: 12, borderRadius: 6, marginBottom: 8, cursor: 'pointer', background: sel?.id === u.id ? '#312e81' : '#0f172a', border: sel?.id === u.id ? '1px solid #6366f1' : '1px solid #334155' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>{u.email || 'No email'}</span>
                    <span style={{ color: '#a5b4fc' }}>{u.credits} credits</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                    {[u.competitive && 'Comp', u.market_research && 'Market', u.customer_research && 'Customer'].filter(Boolean).join(' | ') || 'No access'}
                  </div>
                </div>
              ))}
              {!loading && users.length === 0 && <p style={{ textAlign: 'center', color: '#64748b' }}>No users</p>}
            </div>
          </div>
          <div style={{ background: '#1e293b', borderRadius: 12, padding: 16 }}>
            {sel ? (
              <>
                <h3 style={{ marginTop: 0 }}>{sel.email || 'No email'}</h3>
                <p style={{ color: '#64748b', fontSize: 12, marginBottom: 16 }}>ID: {sel.id}</p>
                <p style={{ fontSize: 24, fontWeight: 'bold', color: '#a5b4fc', marginBottom: 16 }}>{sel.credits} credits</p>
                <div style={{ marginBottom: 24 }}>
                  <h4>Add/Deduct Credits</h4>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input type="number" placeholder="Amount (+/-)" value={amt} onChange={e => setAmt(e.target.value)}
                      style={{ flex: 1, padding: 8, borderRadius: 6, border: '1px solid #334155', background: '#0f172a', color: '#fff' }} />
                    <button onClick={addCredits} disabled={loading || !amt} style={{ padding: '8px 16px', background: '#22c55e', border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer' }}>Apply</button>
                  </div>
                </div>
                <div>
                  <h4>Permissions</h4>
                  {['competitive', 'market_research', 'customer_research'].map(k => (
                    <label key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 8, background: '#0f172a', borderRadius: 6, marginBottom: 8, cursor: 'pointer' }}>
                      <span style={{ textTransform: 'capitalize' }}>{k.replace('_', ' ')}</span>
                      <input type="checkbox" checked={perms[k as keyof typeof perms]} onChange={e => setPerms({ ...perms, [k]: e.target.checked })} />
                    </label>
                  ))}
                  <button onClick={savePerms} disabled={loading} style={{ width: '100%', marginTop: 8, padding: 10, background: '#3b82f6', border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer' }}>Save Permissions</button>
                  <button onClick={deleteUser} disabled={loading} style={{ width: '100%', marginTop: 10, padding: 10, background: '#dc2626', border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer' }}>Delete User</button>
                </div>
                <div style={{ marginTop: 24 }}>
                  <h4 style={{ margin: '0 0 12px 0' }}>Usage Analytics</h4>
                  {userAnalyticsLoading ? (
                    <div style={{ color: '#94a3b8', fontSize: 14 }}>Loading analytics...</div>
                  ) : userAnalytics ? (
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      {WINDOWS.map(window => (
                        <div key={window} style={{
                          background: '#1f2937',
                          padding: 12,
                          borderRadius: 8,
                          minWidth: 120,
                          flex: 1
                        }}>
                          <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#9ca3af' }}>
                            {window.replace(/_/g, ' ')}
                          </div>
                          <div style={{ fontSize: 14, marginTop: 4, color: '#cbd5e1' }}>
                            {userAnalytics.windows[window].total_microreports} microreports
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ color: '#94a3b8', fontSize: 14 }}>0 microreports</div>
                  )}
                </div>
              </>
            ) : <p style={{ textAlign: 'center', color: '#64748b' }}>Select a user</p>}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
